import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { storage, waitForAuth } from './firebase.js';

const MeetingMinutesPage = ({ user, isAdmin, isMobile }) => {
  const [files, setFiles] = useState([]);
  const [pendingDescriptions, setPendingDescriptions] = useState({});
  const [saveStatus, setSaveStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);

  const fetchDescriptions = useCallback(async (token, currentFiles) => {
    try {
      const projectId = 'nantahala-mountain-resor-596d9';
      const databaseId = 'nmrdb';
      const firestoreUrl = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/${databaseId}/documents/file_descriptions`;
      console.log('Fetching descriptions from Firestore via REST API');
      const fetchResponse = await fetch(firestoreUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!fetchResponse.ok) {
        const errorText = await fetchResponse.text();
        throw new Error(`Firestore REST API fetch error: ${fetchResponse.statusText} - ${errorText}`);
      }
      const fetchData = await fetchResponse.json();
      const descriptionDocs = fetchData.documents || [];
      console.log('Firestore REST API fetch result:', descriptionDocs.map(doc => ({
        name: doc.name,
        filename: doc.fields?.filename?.stringValue,
        description: doc.fields?.description?.stringValue,
        path: doc.fields?.path?.stringValue
      })));

      const descriptionsData = {};
      descriptionDocs.forEach(doc => {
        const fields = doc.fields;
        const path = fields.path?.stringValue;
        const filename = fields.filename?.stringValue;
        const description = fields.description?.stringValue || '';
        if (path === 'Meeting Minutes') {
          descriptionsData[filename] = description;
        }
      });

      currentFiles.forEach(file => {
        if (!(file.name in descriptionsData)) {
          descriptionsData[file.name] = '';
        }
      });

      console.log('Updated descriptionsData:', descriptionsData);
      setPendingDescriptions(prev => {
        if (JSON.stringify(prev) !== JSON.stringify(descriptionsData)) {
          return descriptionsData;
        }
        return prev;
      });
    } catch (err) {
      console.error('Error fetching descriptions:', err);
      setError('Failed to load descriptions: ' + err.message);
    }
  }, []);

  useEffect(() => {
    if (hasFetched.current) {
      return;
    }
    const fetchMeetingMinutes = async () => {
      setLoading(true);
      setError(null);
      try {
        await waitForAuth();
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('User not authenticated');
        }
        const token = await currentUser.getIdToken(true);
        console.log('Refreshed token for Meeting Minutes:', token);
        const idTokenResult = await currentUser.getIdTokenResult();
        console.log('Token claims:', idTokenResult.claims);

        console.log('Attempting to list files from Meeting Minutes via SDK');
        const storageRef = ref(storage, 'Meeting Minutes');
        const listResult = await listAll(storageRef);
        console.log('ListAll result:', listResult.items.map(item => item.name));

        const fileList = await Promise.all(
          listResult.items.map(async (item) => {
            const url = await getDownloadURL(item);
            return { name: item.name, url };
          })
        );
        console.log('Files fetched:', fileList);
        setFiles(fileList);

        console.log('Checking if file_descriptions collection exists via REST API');
        const projectId = 'nantahala-mountain-resor-596d9';
        const databaseId = 'nmrdb';
        const firestoreUrl = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/${databaseId}/documents/file_descriptions`;
        console.log('Firestore REST API URL:', firestoreUrl);
        const response = await fetch(firestoreUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        console.log('Firestore REST API response status:', response.status);
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Firestore REST API error response:', errorText);
          throw new Error(`Firestore REST API error: ${response.statusText} - ${errorText}`);
        }
        const data = await response.json();
        console.log('Firestore REST API response:', data);

        let descriptionDocs = data.documents || [];
        if (descriptionDocs.length === 0) {
          console.log('file_descriptions collection is empty or does not exist; creating it');
          const dummyDocUrl = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/${databaseId}/documents/file_descriptions/dummy`;
          const dummyDocResponse = await fetch(dummyDocUrl, {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              fields: {
                filename: { stringValue: 'dummy' },
                description: { stringValue: 'Dummy document to initialize collection' },
                path: { stringValue: 'dummy' }
              }
            })
          });
          console.log('Dummy document creation response status:', dummyDocResponse.status);
          if (!dummyDocResponse.ok) {
            const errorText = await dummyDocResponse.text();
            throw new Error(`Failed to create dummy document: ${dummyDocResponse.statusText} - ${errorText}`);
          }
          console.log('Dummy document created in file_descriptions via REST API');
        } else {
          console.log('file_descriptions collection exists with documents:', descriptionDocs.map(doc => doc.name));
        }

        // Fetch descriptions after initializing the collection
        await fetchDescriptions(token, fileList);
        setLoading(false);
        hasFetched.current = true;
      } catch (err) {
        console.error('Error fetching meeting minutes:', err);
        setError('Failed to load meeting minutes: ' + err.message);
        setLoading(false);
      }
    };

    fetchMeetingMinutes();
  }, [fetchDescriptions]);

  console.log("MeetingMinutesPage rendering with isAdmin:", isAdmin);

  const handleDescriptionChange = (filename, newDescription) => {
    setPendingDescriptions(prev => ({
      ...prev,
      [filename]: newDescription
    }));
    setSaveStatus(prev => ({
      ...prev,
      [filename]: 'unsaved'
    }));
  };

  const handleSave = async (filename) => {
    try {
      setSaveStatus(prev => ({
        ...prev,
        [filename]: 'saving'
      }));
      await waitForAuth();
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      const token = await currentUser.getIdToken(true);
      console.log('Refreshed token before Firestore save:', token);
      const idTokenResult = await currentUser.getIdTokenResult();
      console.log('Token claims before save:', idTokenResult.claims);

      const newDescription = pendingDescriptions[filename];
      console.log(`Saving description for ${filename}: ${newDescription}`);
      const projectId = 'nantahala-mountain-resor-596d9';
      const databaseId = 'nmrdb';
      const sanitizedFilename = filename.replace(/[^a-zA-Z0-9_-]/g, '_');
      const docId = `${sanitizedFilename}_MeetingMinutes`;
      const docUrl = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/${databaseId}/documents/file_descriptions/${docId}`;
      const saveResponse = await fetch(docUrl, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fields: {
            filename: { stringValue: filename },
            description: { stringValue: newDescription },
            path: { stringValue: 'Meeting Minutes' }
          }
        })
      });
      console.log('Save description response status:', saveResponse.status);
      if (!saveResponse.ok) {
        const errorText = await saveResponse.text();
        console.error('Save description error:', errorText);
        throw new Error(`Failed to save description: ${saveResponse.statusText} - ${errorText}`);
      }
      console.log('Description saved successfully via REST API');

      // Re-fetch descriptions to update the UI
      await fetchDescriptions(token, files);

      setSaveStatus(prev => ({
        ...prev,
        [filename]: 'saved'
      }));
    } catch (err) {
      console.error('Error saving description:', err);
      setSaveStatus(prev => ({
        ...prev,
        [filename]: 'error'
      }));
      setError('Failed to save description: ' + err.message);
    }
  };

  console.log('Rendering MeetingMinutesPage with files:', files);

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  if (error) {
    return <div className="container error-message">{error}</div>;
  }

  return (
    <div className="container">
      <style>
        {`
          .container {
            margin: 0 auto;
            padding: 1rem;
            opacity: 1;
          }
          .page-title {
            font-size: 1.5rem;
            font-weight: bold;
            margin-bottom: 1rem;
          }
          .no-files {
            font-size: 1rem;
          }
          .error-message {
            color: #ef4444;
          }
          .table-container {
            overflow-x: auto;
            position: relative;
            z-index: 1;
          }
          .doc-table {
            min-width: 100%;
            border-collapse: collapse;
          }
          .table-header {
            background: #1a3c34;
            color: white;
          }
          .table-header-cell {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            text-align: left;
            white-space: nowrap;
          }
          .table-row:nth-child(even) {
            background: #f3f4f6;
          }
          .table-row:nth-child(odd) {
            background: #e5e7eb;
          }
          .table-cell {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            white-space: pre-line;
          }
          .file-link {
            color: #3b82f6;
          }
          .file-link:hover {
            text-decoration: underline;
          }
          .description-input {
            width: 100%;
            border: 1px solid #d1d5db;
            border-radius: 0.25rem;
            padding: 0.25rem;
            font-size: 1rem;
          }
          .save-button {
            background: #3b82f6;
            color: white;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
          }
          .save-button:hover {
            background: #2563eb;
          }
          .save-button:disabled {
            background: #93c5fd;
          }
          .status-saved {
            color: #22c55e;
          }
          .status-error {
            color: #ef4444;
          }
          .status-unsaved {
            color: #eab308;
          }
          @media (max-width: 640px) {
            .container {
              padding: 0.5rem;
            }
            .page-title {
              font-size: 1.25rem;
            }
            .no-files {
              font-size: 0.875rem;
            }
            .error-message {
              font-size: 0.875rem;
            }
            .table-header-cell,
            .table-cell {
              padding: 0.4rem;
              font-size: 0.875rem;
            }
            .description-input {
              font-size: 0.9rem;
              padding: 0.2rem;
            }
            .save-button {
              padding: 0.2rem 0.4rem;
              font-size: 0.9rem;
              width: 100%;
              text-align: center;
            }
            .status-saved,
            .status-error,
            .status-unsaved {
              font-size: 0.875rem;
            }
          }
        `}
      </style>
      <h1 className="page-title">Meeting Minutes</h1>
      {files.length === 0 ? (
        <p className="no-files">No meeting minutes available.</p>
      ) : (
        <div className="table-container">
          <table className="doc-table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Filename</th>
                <th className="table-header-cell">Description</th>
                {isAdmin && <th className="table-header-cell">Action</th>}
                {isAdmin && <th className="table-header-cell">Status</th>}
              </tr>
            </thead>
            <tbody>
              {files.map((file) => {
                console.log(`Rendering row for file: ${file.name}`);
                return (
                  <tr key={file.name} className="table-row">
                    <td className="table-cell">
                      <a
                        href={file.url}
                        download
                        className="file-link"
                      >
                        {file.name}
                      </a>
                    </td>
                    <td className="table-cell">
                      {isAdmin ? (
                        <input
                          type="text"
                          value={pendingDescriptions[file.name] || ''}
                          onChange={(e) => handleDescriptionChange(file.name, e.target.value)}
                          className="description-input"
                          placeholder="Enter description"
                        />
                      ) : (
                        pendingDescriptions[file.name] || ''
                      )}
                    </td>
                    {isAdmin && (
                      <td className="table-cell">
                        <button
                          onClick={() => handleSave(file.name)}
                          className="save-button"
                          disabled={saveStatus[file.name] === 'saving'}
                        >
                          {saveStatus[file.name] === 'saving' ? 'Saving...' : 'Save'}
                        </button>
                      </td>
                    )}
                    {isAdmin && (
                      <td className="table-cell">
                        {saveStatus[file.name] === 'saved' && <span className="status-saved">Saved</span>}
                        {saveStatus[file.name] === 'error' && <span className="status-error">Error</span>}
                        {saveStatus[file.name] === 'unsaved' && <span className="status-unsaved">Unsaved</span>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MeetingMinutesPage;